import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useConfirm } from "@/components/ConfirmDialog";
import {
  ArrowLeft, Users, Crown, Shield, Wand2, BarChart3,
  Plus, Trash2, ChevronUp, ChevronDown, Loader2, Search, Calendar, Clock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tierIcons = { commoner: Shield, adventurer: Crown, archmage: Wand2 };
const tierColors = {
  commoner: "bg-stone-700 text-stone-200",
  adventurer: "bg-amber-700 text-amber-100",
  archmage: "bg-violet-700 text-violet-100",
};

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ email: "", name: "", tier: "commoner" });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    checkAdmin();
  }, []);

  const checkAdmin = async () => {
    try {
      const res = await axios.get(`${API}/admin/check`, { withCredentials: true });
      if (!res.data.is_admin) {
        toast.error("Admin access required");
        navigate("/dashboard");
        return;
      }
      fetchData();
    } catch {
      navigate("/dashboard");
    }
  };

  const fetchData = async () => {
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.name) {
      toast.error("Email and name are required");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users/create`, createForm, { withCredentials: true });
      toast.success(`Account created for ${createForm.email}`);
      setCreateOpen(false);
      setCreateForm({ email: "", name: "", tier: "commoner" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleChangeTier = async (userId, newTier) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/tier`, { tier: newTier }, { withCredentials: true });
      toast.success(`Tier updated to ${newTier}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update tier");
    }
  };

  const handleDeleteUser = async (userId, email) => {
    const ok = await confirm("Delete User", `Are you sure you want to delete ${email}? This cannot be undone.`);
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success(`User ${email} deleted`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border/50 bg-card/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-muted-foreground" data-testid="admin-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
            </Button>
            <h1 className="text-xl font-serif font-bold">Admin Panel</h1>
          </div>
          <span className="text-sm text-muted-foreground">{user?.email}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            <StatCard label="Total Users" value={stats.total_users} icon={Users} />
            <StatCard label="Commoners" value={stats.tiers.commoner} icon={Shield} color="text-stone-400" />
            <StatCard label="Adventurers" value={stats.tiers.adventurer} icon={Crown} color="text-amber-400" />
            <StatCard label="Archmages" value={stats.tiers.archmage} icon={Wand2} color="text-violet-400" />
          </div>
        )}

        {/* Extra stats row */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            <StatCard label="Total Campaigns" value={stats.total_campaigns} icon={BarChart3} />
            <StatCard label="Total Payments" value={stats.transactions.total} icon={BarChart3} />
            <StatCard label="Paid Transactions" value={stats.transactions.paid} icon={BarChart3} color="text-green-400" />
          </div>
        )}

        {/* User Management */}
        <div className="bg-card border border-border/50 rounded-lg overflow-hidden" data-testid="admin-users-section">
          <div className="p-4 border-b border-border/50 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <h2 className="text-lg font-serif font-bold flex items-center gap-2">
              <Users className="w-5 h-5" /> Users ({users.length})
            </h2>
            <div className="flex gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:flex-none">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 w-full sm:w-64"
                  data-testid="admin-search-users"
                />
              </div>
              <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="shrink-0" data-testid="admin-create-user-btn">
                    <Plus className="w-4 h-4 mr-1" /> Create Account
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-serif">Create Account</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4 mt-2">
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Email</label>
                      <Input
                        type="email"
                        value={createForm.email}
                        onChange={e => setCreateForm(p => ({...p, email: e.target.value}))}
                        placeholder="friend@email.com"
                        required
                        data-testid="admin-create-email"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Display Name</label>
                      <Input
                        value={createForm.name}
                        onChange={e => setCreateForm(p => ({...p, name: e.target.value}))}
                        placeholder="Gandalf"
                        required
                        data-testid="admin-create-name"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground uppercase tracking-wider">Tier</label>
                      <div className="flex gap-2 mt-1">
                        {["commoner", "adventurer", "archmage"].map(t => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => setCreateForm(p => ({...p, tier: t}))}
                            className={`flex-1 py-2 px-3 rounded-sm border text-sm capitalize transition-colors ${
                              createForm.tier === t
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border text-muted-foreground hover:border-muted-foreground"
                            }`}
                            data-testid={`admin-create-tier-${t}`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Button type="submit" disabled={creating} className="w-full" data-testid="admin-create-submit">
                      {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                      Create Account
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50 text-muted-foreground text-left">
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Tier</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Joined</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Sub Expires</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Campaigns</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Storage</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(u => {
                  const TierIcon = tierIcons[u.tier] || Shield;
                  return (
                    <tr key={u.user_id} className="border-b border-border/30 hover:bg-muted/10" data-testid={`admin-user-row-${u.user_id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold">
                              {u.name?.[0]?.toUpperCase() || "?"}
                            </div>
                          )}
                          <div>
                            <div className="font-medium">{u.name}</div>
                            <div className="text-xs text-muted-foreground">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${tierColors[u.tier] || tierColors.commoner}`}>
                          <TierIcon className="w-3 h-3" />
                          <span className="capitalize">{u.tier}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {u.subscription?.expires_at ? (
                          <span className={`text-xs flex items-center gap-1 ${new Date(u.subscription.expires_at) < new Date() ? 'text-red-400' : 'text-green-400'}`}>
                            <Clock className="w-3 h-3" />
                            {new Date(u.subscription.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">{u.campaigns_count}</td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        {(u.storage_used_bytes / (1024*1024)).toFixed(1)} MB
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          {["commoner", "adventurer", "archmage"].filter(t => t !== u.tier).map(t => (
                            <Button
                              key={t}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleChangeTier(u.user_id, t)}
                              className="text-xs capitalize h-7 px-2"
                              title={`Set to ${t}`}
                              data-testid={`admin-set-tier-${u.user_id}-${t}`}
                            >
                              {t === "archmage" ? <ChevronUp className="w-3 h-3 mr-1 text-violet-400" /> :
                               t === "adventurer" ? <ChevronUp className="w-3 h-3 mr-1 text-amber-400" /> :
                               <ChevronDown className="w-3 h-3 mr-1 text-stone-400" />}
                              {t}
                            </Button>
                          ))}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(u.user_id, u.email)}
                            className="text-red-400 hover:text-red-300 h-7 px-2"
                            data-testid={`admin-delete-${u.user_id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      {search ? "No users match your search" : "No users found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color = "text-primary" }) => (
  <div className="bg-card border border-border/50 rounded-lg p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs text-muted-foreground uppercase tracking-wider">{label}</span>
      <Icon className={`w-4 h-4 ${color}`} />
    </div>
    <div className="text-2xl font-bold">{value}</div>
  </div>
);

export default AdminPage;
